import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { costsApi } from '@/utils/api';
import type {
  OperationalCostEntry,
  ProgramBudgetResponse,
  ProgramCostSummary,
  CostDashboardData,
  CostCategory,
} from '@/types';
import type { RootState } from '../index';

interface CostsState {
  // Program-specific budget data
  currentProgramBudget: ProgramBudgetResponse | null;
  // Cost entries for current program
  costEntries: OperationalCostEntry[];
  // Program cost summary
  costSummary: ProgramCostSummary | null;
  // Executive dashboard data
  dashboardData: CostDashboardData | null;
  // Filters
  filters: {
    fiscalYear: number | null;
    quarter: number | null;
    category: CostCategory | null;
  };
  // Loading states
  isLoadingBudget: boolean;
  isLoadingEntries: boolean;
  isLoadingSummary: boolean;
  isLoadingDashboard: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  // Error state
  error: string | null;
}

const initialState: CostsState = {
  currentProgramBudget: null,
  costEntries: [],
  costSummary: null,
  dashboardData: null,
  filters: {
    fiscalYear: new Date().getFullYear(),
    quarter: null,
    category: null,
  },
  isLoadingBudget: false,
  isLoadingEntries: false,
  isLoadingSummary: false,
  isLoadingDashboard: false,
  isSaving: false,
  isDeleting: false,
  error: null,
};

// Async thunks

// Fetch program budget
export const fetchProgramBudget = createAsyncThunk(
  'costs/fetchBudget',
  async (programId: string, { rejectWithValue }) => {
    try {
      const response = await costsApi.getBudget(programId);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch budget');
      }
      return response.data as ProgramBudgetResponse;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch budget');
    }
  }
);

// Update program budget
export const updateProgramBudget = createAsyncThunk(
  'costs/updateBudget',
  async (
    {
      programId,
      data,
    }: {
      programId: string;
      data: {
        annualBudget?: number;
        operationalOverhead?: number;
        laborBudget?: number;
        nonLaborBudget?: number;
        budgetFiscalYear?: number;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await costsApi.updateBudget(programId, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update budget');
      }
      return response.data as ProgramBudgetResponse;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update budget');
    }
  }
);

// Fetch cost entries for a program
export const fetchCostEntries = createAsyncThunk(
  'costs/fetchEntries',
  async (
    {
      programId,
      params,
    }: {
      programId: string;
      params?: { fiscalYear?: number; quarter?: number; category?: string };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await costsApi.getCostEntries(programId, params);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch cost entries');
      }
      const data = response.data as any;
      return (data?.costs || data?.entries || data?.costEntries || []) as OperationalCostEntry[];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch cost entries');
    }
  }
);

// Create cost entry
export const createCostEntry = createAsyncThunk(
  'costs/createEntry',
  async (
    {
      programId,
      data,
    }: {
      programId: string;
      data: {
        category: string;
        description: string;
        amount: number;
        fiscalYear: number;
        quarter: number;
        month?: number;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await costsApi.createCostEntry(programId, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to create cost entry');
      }
      const respData = response.data as any;
      return (respData?.cost || respData?.entry || respData?.costEntry || respData) as OperationalCostEntry;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create cost entry');
    }
  }
);

// Update cost entry
export const updateCostEntry = createAsyncThunk(
  'costs/updateEntry',
  async (
    {
      costId,
      data,
    }: {
      costId: string;
      data: {
        category?: string;
        description?: string;
        amount?: number;
        fiscalYear?: number;
        quarter?: number;
        month?: number;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await costsApi.updateCostEntry(costId, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update cost entry');
      }
      const respData = response.data as any;
      return (respData?.cost || respData?.entry || respData?.costEntry || respData) as OperationalCostEntry;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update cost entry');
    }
  }
);

// Delete cost entry
export const deleteCostEntry = createAsyncThunk(
  'costs/deleteEntry',
  async (costId: string, { rejectWithValue }) => {
    try {
      const response = await costsApi.deleteCostEntry(costId);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to delete cost entry');
      }
      return costId;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to delete cost entry');
    }
  }
);

// Fetch cost summary for a program
export const fetchCostSummary = createAsyncThunk(
  'costs/fetchSummary',
  async (programId: string, { rejectWithValue }) => {
    try {
      const response = await costsApi.getCostSummary(programId);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch cost summary');
      }
      const data = response.data as any;
      return (data?.summary || data) as ProgramCostSummary;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch cost summary');
    }
  }
);

// Fetch cost dashboard (executive view)
export const fetchCostDashboard = createAsyncThunk(
  'costs/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await costsApi.getCostDashboard();
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch cost dashboard');
      }
      const data = response.data as any;
      return (data?.dashboard || data) as CostDashboardData;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch cost dashboard');
    }
  }
);

const costsSlice = createSlice({
  name: 'costs',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (
      state,
      action: PayloadAction<Partial<CostsState['filters']>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        fiscalYear: new Date().getFullYear(),
        quarter: null,
        category: null,
      };
    },
    clearProgramData: (state) => {
      state.currentProgramBudget = null;
      state.costEntries = [];
      state.costSummary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch budget
      .addCase(fetchProgramBudget.pending, (state) => {
        state.isLoadingBudget = true;
        state.error = null;
      })
      .addCase(fetchProgramBudget.fulfilled, (state, action) => {
        state.isLoadingBudget = false;
        state.currentProgramBudget = action.payload;
      })
      .addCase(fetchProgramBudget.rejected, (state, action) => {
        state.isLoadingBudget = false;
        state.error = action.payload as string;
      })
      // Update budget
      .addCase(updateProgramBudget.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateProgramBudget.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentProgramBudget = action.payload;
      })
      .addCase(updateProgramBudget.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      // Fetch entries
      .addCase(fetchCostEntries.pending, (state) => {
        state.isLoadingEntries = true;
        state.error = null;
      })
      .addCase(fetchCostEntries.fulfilled, (state, action) => {
        state.isLoadingEntries = false;
        state.costEntries = action.payload || [];
      })
      .addCase(fetchCostEntries.rejected, (state, action) => {
        state.isLoadingEntries = false;
        state.error = action.payload as string;
      })
      // Create entry
      .addCase(createCostEntry.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createCostEntry.fulfilled, (state, action) => {
        state.isSaving = false;
        if (action.payload) {
          state.costEntries.unshift(action.payload);
        }
      })
      .addCase(createCostEntry.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      // Update entry
      .addCase(updateCostEntry.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateCostEntry.fulfilled, (state, action) => {
        state.isSaving = false;
        if (action.payload) {
          const index = state.costEntries.findIndex(
            (e) => e.id === action.payload!.id
          );
          if (index !== -1) {
            state.costEntries[index] = action.payload;
          }
        }
      })
      .addCase(updateCostEntry.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      // Delete entry
      .addCase(deleteCostEntry.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteCostEntry.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.costEntries = state.costEntries.filter(
          (e) => e.id !== action.payload
        );
      })
      .addCase(deleteCostEntry.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      // Fetch summary
      .addCase(fetchCostSummary.pending, (state) => {
        state.isLoadingSummary = true;
        state.error = null;
      })
      .addCase(fetchCostSummary.fulfilled, (state, action) => {
        state.isLoadingSummary = false;
        state.costSummary = action.payload;
      })
      .addCase(fetchCostSummary.rejected, (state, action) => {
        state.isLoadingSummary = false;
        state.error = action.payload as string;
      })
      // Fetch dashboard
      .addCase(fetchCostDashboard.pending, (state) => {
        state.isLoadingDashboard = true;
        state.error = null;
      })
      .addCase(fetchCostDashboard.fulfilled, (state, action) => {
        state.isLoadingDashboard = false;
        state.dashboardData = action.payload;
      })
      .addCase(fetchCostDashboard.rejected, (state, action) => {
        state.isLoadingDashboard = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setFilters, clearFilters, clearProgramData } =
  costsSlice.actions;

// Selectors
export const selectProgramBudget = (state: RootState) =>
  state.costs.currentProgramBudget;
export const selectCostEntries = (state: RootState) => state.costs.costEntries;
export const selectCostSummary = (state: RootState) => state.costs.costSummary;
export const selectCostDashboard = (state: RootState) =>
  state.costs.dashboardData;
export const selectCostFilters = (state: RootState) => state.costs.filters;
export const selectCostsLoading = createSelector(
  [
    (state: RootState) => state.costs.isLoadingBudget,
    (state: RootState) => state.costs.isLoadingEntries,
    (state: RootState) => state.costs.isLoadingSummary,
    (state: RootState) => state.costs.isLoadingDashboard,
    (state: RootState) => state.costs.isSaving,
    (state: RootState) => state.costs.isDeleting,
  ],
  (budget, entries, summary, dashboard, saving, deleting) => ({
    budget,
    entries,
    summary,
    dashboard,
    saving,
    deleting,
  })
);
export const selectCostsError = (state: RootState) => state.costs.error;

// Computed selectors (memoized)
export const selectTotalCostsByCategory = createSelector(
  [selectCostEntries],
  (entries) => {
    const totals: Record<string, number> = {};
    entries.forEach((entry) => {
      totals[entry.category] = (totals[entry.category] || 0) + entry.amount;
    });
    return totals;
  }
);

export const selectTotalCostsByQuarter = createSelector(
  [selectCostEntries],
  (entries) => {
    const totals: Record<number, number> = {};
    entries.forEach((entry) => {
      totals[entry.quarter] = (totals[entry.quarter] || 0) + entry.amount;
    });
    return totals;
  }
);

export const selectBudgetUtilization = (state: RootState) => {
  const budget = state.costs.currentProgramBudget;
  if (!budget || !budget.budget.annual) return null;
  return budget.utilizationPercent;
};

export default costsSlice.reducer;
